import type { FamilyPaymentsRuleSet } from "./types.js";

// family-payments rate period for the 2025-26 financial year, keyed by the
// 20 September 2025 indexation date.
//
// Services Australia indexes FTB rates and income-test thresholds on 20 March
// and 20 September each year, NOT on the 1 July FY boundary — so this ruleset is
// DATE-keyed (rates-YYYY-MM-DD.ts), mirroring the au13 date-period convention.
// A new indexation gets a NEW file registered alongside this one; never mutate a
// shipped rate period (historical reconciliations must resolve against the rates
// in force then).
//
// VERIFICATION (env-AGENT_VERIFIED — corroborated across ≥2-3 independent
// 2025-26 secondaries; the DSS guide (403) and Services Australia (timeout) were
// network-unreachable this run, so these are CITED-equivalent pending a P4
// browser re-check, not strict-CITED). Ledgers:
//   docs/research/discovery/verification/ftb-entitlement-estimator-figures.md
//   docs/research/discovery/verification/ftb-eligibility-gap-checker-figures.md
//   docs/research/discovery/verification/family-payment-debt-risk-figures.md
//
// BUILD FIX (verification ledger figure #6): the FTB Part A max rate for a child
// aged 0-12 is $227.36/fortnight for 2025-26 — NOT the dossier's mis-transcribed
// $222.04. The build MUST use $227.36.
export const rates_2025_09_20: FamilyPaymentsRuleSet = {
  product: "FAMILY_PAYMENTS",
  periodKey: "2025-09-20",
  periodLabel: "FTB rates from 20 September 2025 (FY2025-26)",
  effectiveFrom: "2025-09-20",
  effectiveTo: null,
  sources: {
    partARates:
      "https://www.servicesaustralia.gov.au/family-tax-benefit-part-payment-rates?context=22151",
    partAIncomeTest:
      "https://www.servicesaustralia.gov.au/income-test-for-family-tax-benefit-part?context=22151",
    partBRates:
      "https://www.servicesaustralia.gov.au/family-tax-benefit-part-b-payment-rates?context=22151",
    partBIncomeTest:
      "https://www.servicesaustralia.gov.au/income-test-for-family-tax-benefit-part-b?context=22151",
    supplements:
      "https://guides.dss.gov.au/family-assistance-guide/3/1/1/20",
    ccsWithholding:
      "https://www.servicesaustralia.gov.au/balancing-child-care-subsidy?context=41186",
  },
  sourcesVerifiedAt: "2026-06-22",

  // Services Australia FTB Part A payment rates, per fortnight, 2025-26.
  // BUILD FIX: 0-12 = $227.36 (ledger figure #6 correction — not $222.04).
  partAMaxRates: {
    age0to12: 227.36,
    age13to19: 295.82,
  },

  // FTB Part A income test (Method 1, two-stage), 2025-26.
  //   - Max rate applies up to the lower free area ($66,722).
  //   - Above it, the max rate reduces 20c/$1 down to the base rate.
  //   - The base rate ($73.08/ft/child) keeps paying up to the higher free area
  //     ($118,771).
  //   - Above the higher free area the base rate reduces 30c/$1 to nil.
  partAIncomeTest: {
    lowerFreeArea: 66_722,
    lowerTaperPerDollar: 0.2,
    higherFreeArea: 118_771,
    higherTaperPerDollar: 0.3,
    baseRatePerChild: 73.08,
  },

  // FTB Part B income test, 2025-26.
  //   - Primary-earner ATI limit $120,007 is a CLIFF: above it, no Part B.
  //   - Secondary-earner free area $6,935, then 20c/$1 taper.
  partBIncomeTest: {
    primaryEarnerLimit: 120_007,
    secondaryEarnerFreeArea: 6_935,
    secondaryTaperPerDollar: 0.2,
    // Part B max rates per fortnight, 2025-26.
    maxRateYoungestUnder5: 193.34,
    maxRateYoungest5to18: 134.96,
  },

  // End-of-year supplements, 2025-26. BOTH gated by the $80,000 ATI cap —
  // all-or-nothing, NO taper. Above $80k combined ATI the supplement is nil and
  // does NOT persist (copy must never imply it does).
  supplements: {
    partAPerChild: 938.05,
    partBPerFamily: 459.9,
    supplementIncomeCap: 80_000,
  },

  // CCS auto-withholds a default 5% of the subsidy as overpayment protection;
  // FTB has NO equivalent instalment withholding. The family can adjust the %.
  ccsWithholding: {
    defaultRate: 0.05,
  },

  fortnightsPerYear: 26,
};
